import { useEffect, useState } from 'react';
import {
  Pencil, Search, CalendarCheck, Loader2, ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import { adminAPI, mainCategoriesAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { toSASTDateInput } from '@/lib/tz';
import { confirmWindowImpact } from '@/lib/windowImpact';
import { WindowHistoryList } from '@/components/admin/WindowHistoryList';

const SORT_OPTIONS = [
  { key: 'name', label: 'Name (A–Z)' },
  { key: 'close_soonest', label: 'Window closes soonest' },
];
const SORT_STORAGE_KEY = 'avz.adminWindows.sortBy';

export default function AdminWindows() {
  const [vendors, setVendors] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState(() => {
    if (typeof window === 'undefined') return 'name';
    const saved = window.localStorage.getItem(SORT_STORAGE_KEY);
    return SORT_OPTIONS.some(o => o.key === saved) ? saved : 'name';
  });
  const [editingVendor, setEditingVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ window_open_date: '', window_close_date: '' });

  const fetchData = () => {
    setLoading(true);
    adminAPI.listWindows()
      .then(r => setVendors(r.data))
      .catch(() => toast.error('Failed to load order windows'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    let active = true;
    adminAPI.listWindows()
      .then(r => { if (active) setVendors(r.data); })
      .catch(() => toast.error('Failed to load order windows'))
      .finally(() => { if (active) setLoading(false); });
    mainCategoriesAPI.list().then(r => { if (active) setMainCategories(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const persistSort = (key) => {
    setSortBy(key);
    try { window.localStorage.setItem(SORT_STORAGE_KEY, key); } catch { /* ignore */ }
  };

  // window state — 'open' | 'upcoming' | 'closed' | null (no dates)
  const windowState = (v) => {
    const open = v.window_open_date || '';
    const close = v.window_close_date || '';
    if (!open || !close) return null;
    const now = new Date().toISOString();
    if (now < open) return 'upcoming';
    if (now > close) return 'closed';
    return 'open';
  };
  const isDerivedActive = (v) => v.is_active !== false && windowState(v) === 'open';

  const sortedVendors = [...vendors].sort((a, b) => {
    if (sortBy === 'close_soonest') {
      const aC = a.window_close_date || '';
      const bC = b.window_close_date || '';
      if (aC && bC && aC !== bC) return aC < bC ? -1 : 1;
      if (aC && !bC) return -1;
      if (!aC && bC) return 1;
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  const matchesSearch = (v) =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.contact_email || '').toLowerCase().includes(search.toLowerCase());

  const allFiltered = sortedVendors.filter(matchesSearch);
  const activeVendors = allFiltered.filter(isDerivedActive);
  const inactiveVendors = allFiltered.filter(v => !isDerivedActive(v));

  const visibleActive = activeFilter === 'inactive' ? [] : activeVendors;
  const visibleInactive = activeFilter === 'active' ? [] : inactiveVendors;
  const totalVisible = visibleActive.length + visibleInactive.length;

  const fmtDate = (iso) => {
    if (!iso) return '';
    // Format the calendar date in UTC so it always matches the date the admin
    // picked, regardless of the viewer's local timezone (avoids off-by-one).
    try { return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', timeZone: 'Africa/Johannesburg' }); }
    catch { return iso.slice(0, 10); }
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setForm({
      window_open_date: toSASTDateInput(vendor.window_open_date),
      window_close_date: toSASTDateInput(vendor.window_close_date),
    });
  };

  const handleSave = async () => {
    if (!form.window_open_date || !form.window_close_date) {
      toast.error('Both dates are required');
      return;
    }
    const changed = form.window_open_date !== toSASTDateInput(editingVendor.window_open_date)
      || form.window_close_date !== toSASTDateInput(editingVendor.window_close_date);
    if (changed && !(await confirmWindowImpact(editingVendor.id, form.window_open_date, form.window_close_date))) return;
    setSaving(true);
    try {
      await adminAPI.updateSupplier(editingVendor.id, {
        // Store as UTC start/end-of-day so display (also UTC) is stable and
        // matches the picked calendar date for every viewer timezone.
        window_open_date: `${form.window_open_date}T00:00:00Z`,
        window_close_date: `${form.window_close_date}T23:59:59Z`,
      });
      toast.success(`Window updated for ${editingVendor.name}`);
      setEditingVendor(null);
      fetchData();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (v) => {
    const ws = windowState(v);
    const mc = mainCategories.find(m => m.id === v.main_category_id);
    return (
      <div
        key={v.id}
        data-testid={`window-row-${v.id}`}
        className="flex items-center gap-4 p-4 sm:p-5 rounded-2xl bg-card border border-border hover:border-teal/30 transition-colors cursor-pointer group"
        onClick={() => openEdit(v)}
      >
        {v.image_url ? (
          <img
            src={v.image_url}
            alt={v.name}
            className="w-12 h-12 rounded-xl object-contain bg-white/5 flex-shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling;
              if (fb) fb.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`w-12 h-12 rounded-xl bg-teal/10 items-center justify-center flex-shrink-0 ${v.image_url ? 'hidden' : 'flex'}`}
        >
          <span className="font-heading text-lg text-teal">{v.name[0]}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-body text-base font-semibold text-foreground group-hover:text-teal transition-colors">{v.name}</h3>
            {mc && (
              <span
                data-testid={`window-main-${v.id}`}
                className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-vintage/15 text-vintage uppercase tracking-wider"
              >
                {mc.name}
              </span>
            )}
            {v.is_active === false && (
              <span className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-400/15 text-red-400">
                Disabled
              </span>
            )}
            {ws === 'open' && (
              <span data-testid={`window-status-${v.id}`} className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal/15 text-teal inline-flex items-center gap-1 animate-pulse">
                <CalendarCheck className="h-3 w-3" strokeWidth={2.5} /> Window Open
              </span>
            )}
            {ws === 'upcoming' && (
              <span data-testid={`window-status-${v.id}`} className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-gold/15 text-gold">
                Window Upcoming
              </span>
            )}
            {ws === 'closed' && (
              <span data-testid={`window-status-${v.id}`} className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground/40">
                Window Closed
              </span>
            )}
            {ws === null && (
              <span data-testid={`window-status-${v.id}`} className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground/40">
                No Window Set
              </span>
            )}
          </div>
          <p className="font-body text-xs text-foreground/50 mt-0.5 truncate">{v.contact_email || v.description || '—'}</p>
          {(v.window_open_date || v.window_close_date) && (
            <p
              data-testid={`window-dates-${v.id}`}
              className="font-body text-[11px] text-foreground/40 mt-1 inline-flex items-center gap-1"
            >
              <CalendarCheck className="h-3 w-3" strokeWidth={2} />
              {fmtDate(v.window_open_date) || '?'} — {fmtDate(v.window_close_date) || '?'}
            </p>
          )}
        </div>
        <div className="flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            data-testid={`edit-window-${v.id}`}
            onClick={(e) => { e.stopPropagation(); openEdit(v); }}
            className="rounded-full border-border font-body text-xs gap-1.5"
          >
            <Pencil className="h-3 w-3" /> Set Window
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="admin-windows">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Order Windows</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">
            {activeVendors.length} of {vendors.length} vendors with active windows
          </p>
        </div>
        <Link to="/admin/suppliers">
          <Button variant="outline" className="rounded-full font-body text-sm gap-2 border-border">
            Manage Vendors <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      <p className="font-body text-sm text-foreground/60 mb-6">
        Order windows are set per vendor. Use this page for a quick overview, or manage windows from the Vendors page.
      </p>

      {/* Toolbar: search + tabs + sort */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            data-testid="window-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors..."
            className="pl-10 rounded-xl bg-card border-border font-body text-sm"
          />
        </div>
        <div className="inline-flex rounded-full bg-card border border-border p-1" data-testid="window-filter-tabs">
          {[
            { key: 'all', label: 'All', count: activeVendors.length + inactiveVendors.length },
            { key: 'active', label: 'Active', count: activeVendors.length },
            { key: 'inactive', label: 'Inactive', count: inactiveVendors.length },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`window-filter-${t.key}`}
              onClick={() => setActiveFilter(t.key)}
              className={`px-3 sm:px-4 py-1.5 rounded-full font-body text-xs font-semibold uppercase tracking-wider transition-colors ${
                activeFilter === t.key
                  ? 'bg-teal text-white shadow-sm'
                  : 'text-foreground/60 hover:text-foreground'
              }`}
            >
              {t.label}
              <span className={`ml-1.5 font-body text-[10px] ${activeFilter === t.key ? 'text-white/70' : 'text-foreground/40'}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-2" data-testid="window-sort-control">
          <label htmlFor="window-sort" className="font-body text-[11px] uppercase tracking-wider text-foreground/50 hidden sm:inline">
            Sort
          </label>
          <select
            id="window-sort"
            data-testid="window-sort-select"
            value={sortBy}
            onChange={(e) => persistSort(e.target.value)}
            className="rounded-full bg-card border border-border px-3 py-1.5 font-body text-xs font-semibold text-foreground/80 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-teal/30"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />)}</div>
      ) : totalVisible === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <p className="font-heading text-xl text-foreground/50">No vendors found</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="windows-list">
          {visibleActive.length > 0 && (
            <section data-testid="windows-section-active">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                <h2 className="font-body text-xs font-bold uppercase tracking-[0.18em] text-foreground">
                  Active Vendors
                </h2>
                <span className="font-body text-xs text-foreground/40">({visibleActive.length})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
                {visibleActive.map((v) => renderRow(v))}
              </div>
            </section>
          )}
          {visibleInactive.length > 0 && (
            <section data-testid="windows-section-inactive">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-2 w-2 rounded-full bg-foreground/30" />
                <h2 className="font-body text-xs font-bold uppercase tracking-[0.18em] text-foreground/60">
                  Inactive Vendors
                </h2>
                <span className="font-body text-xs text-foreground/40">({visibleInactive.length})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
                {visibleInactive.map((v) => renderRow(v))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Edit Window Dialog */}
      <Dialog open={!!editingVendor} onOpenChange={() => setEditingVendor(null)}>
        <DialogContent className="bg-background rounded-2xl max-w-sm" data-testid="window-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              Set Window — {editingVendor?.name}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-foreground/60">
              Defines when this vendor is open to receive customer orders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-body text-sm text-foreground">Open Date</Label>
              <Input type="date" value={form.window_open_date} onChange={(e) => setForm((f) => ({ ...f, window_open_date: e.target.value }))} className="mt-1 rounded-xl bg-card border-border font-body" data-testid="window-open-input" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground">Close Date</Label>
              <Input type="date" value={form.window_close_date} onChange={(e) => setForm((f) => ({ ...f, window_close_date: e.target.value }))} className="mt-1 rounded-xl bg-card border-border font-body" data-testid="window-close-input" />
            </div>
            <WindowHistoryList history={editingVendor?.window_history} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVendor(null)} className="rounded-full border-border font-body text-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-window-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
